/**
 * Detailed View - Full leaderboard with descriptions
 */

import { useState, useEffect } from 'react';
import { fetchLeaderboardStats, fetchSinRankings } from '../lib/api-client';
import { SinRankCard } from '../components/SinRankCard';
import type { LeaderboardStats, SinRanking } from '../types';

export function DetailedView() {
  const [stats, setStats] = useState<LeaderboardStats | null>(null);
  const [rankings, setRankings] = useState<SinRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();

    // Auto-refresh every 5 minutes (300000ms)
    const interval = setInterval(() => {
      loadData();
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, rankingsResponse] = await Promise.all([
        fetchLeaderboardStats(),
        fetchSinRankings(),
      ]);

      if (statsResponse.success && rankingsResponse.success) {
        setStats(statsResponse.data);
        setRankings(rankingsResponse.data.rankings);
      } else {
        throw new Error('Failed to load leaderboard data');
      }
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load leaderboard data'
      );
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="app-container">
        <div className="matrix-bg"></div>
        <div className="scanlines"></div>
        <div className="glow-orb"></div>

        {/* Corner brackets */}
        <div className="corner-bracket corner-tl"></div>
        <div className="corner-bracket corner-tr"></div>
        <div className="corner-bracket corner-bl"></div>
        <div className="corner-bracket corner-br"></div>

        <div className="loading">
          <div className="loading-icon-lg">⚖️</div>
          <div className="loading-title">FATHER GOVERNANCE</div>
          <div className="loading-text">
            &gt; ASSESSING_SINS // TALLYING_TRANSGRESSIONS...
          </div>
          <div className="loading-subtext">
            &gt; CALCULATING_PENANCE // PLEASE_WAIT...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="matrix-bg"></div>
        <div className="scanlines"></div>
        <div className="glow-orb"></div>

        {/* Corner brackets */}
        <div className="corner-bracket corner-tl"></div>
        <div className="corner-bracket corner-tr"></div>
        <div className="corner-bracket corner-bl"></div>
        <div className="corner-bracket corner-br"></div>

        <div className="error">
          <div className="error-box">
            <div className="error-icon">⚠️</div>
            <div className="error-text">{error}</div>
            <button
              onClick={loadData}
              className="retry-button"
            >
              RETRY
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Visual effects */}
      <div className="matrix-bg"></div>
      <div className="scanlines"></div>
      <div className="glow-orb"></div>

      {/* Corner brackets */}
      <div className="corner-bracket corner-tl"></div>
      <div className="corner-bracket corner-tr"></div>
      <div className="corner-bracket corner-bl"></div>
      <div className="corner-bracket corner-br"></div>

      {/* Header */}
      <header className="header">
        <h1 className="header-title">
          DAO CONFESSION BOOTH
        </h1>
        <p className="header-subtitle">
          A sacred space for weary souls of DAO participants
        </p>

        {stats && (
          <>
            <div className="stats-container">
              <div className="stat-box">
                <div className="stat-number">{stats.totalConfessions}</div>
                <div className="stat-label">Total Confessions</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{stats.totalSins}</div>
                <div className="stat-label">Sins Catalogued</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{stats.totalConfessors}</div>
                <div className="stat-label">Confessors</div>
              </div>
            </div>

            <div className="terminal-text">
              &gt; LAST_UPDATED // {new Date(stats.lastUpdated).toLocaleString()}
            </div>
          </>
        )}
      </header>

      {/* Main Content */}
      <main className="main-content">
        <h2 className="section-title">SIN LEADERBOARD</h2>

        {rankings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📿</div>
            <div className="empty-title">No Confessions Yet</div>
            <div className="empty-text">
              The confessional awaits the first penitent...
            </div>
          </div>
        ) : (
          <div>
            {rankings.map((sin, index) => (
              <SinRankCard key={sin.sinId} sin={sin} rank={index + 1} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
